import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { apiFetch } from '../utils/api';
import { teamAcronym } from '../utils/teamAcronyms';
import Matrix3D from './Matrix3D';

function formatDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  const day = d.getDate();
  const mon = d.toLocaleString('en', { month: 'short' });
  return `${day} ${mon}`;
}

const scoreColorFn = (val, maxVal) => {
  const intensity = Math.min(1, val / maxVal);
  return { color: '#0d6efd', opacity: 0.4 + intensity * 0.5 };
};

export default function ScoreMatrixCard() {
  const { token } = useAuth();
  const { selectedTeam, selectedDate, modelDatesParam } = useDashboard();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || !selectedTeam || !selectedDate) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ team: selectedTeam, date: selectedDate });
    if (modelDatesParam) params.set('modelDates', modelDatesParam);
    apiFetch(`/score-matrix?${params}`, token)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token, selectedTeam, selectedDate, modelDatesParam]);

  const matrix6x6 = data && !data.noMatch && !data.noMatrix
    ? data.matrix.slice(0, 6).map((row) => row.slice(0, 6))
    : null;

  return (
    <div className="card h-100">
      <div className="card-header d-flex align-items-center">
        <strong>Score Prediction</strong>
        <span className="ms-auto text-muted small">Next match</span>
      </div>
      <div className="card-body p-2">
        {loading && (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
        )}
        {error && (
          <div className="text-center py-4 text-danger small">{error}</div>
        )}
        {!loading && !error && data?.noMatch && (
          <div className="text-center py-4 text-muted small">No upcoming match within 7 days</div>
        )}
        {!loading && !error && data && !data.noMatch && data.noMatrix && (
          <div className="text-center py-4 text-muted small">No model prediction available</div>
        )}
        {!loading && !error && matrix6x6 && (
          <>
            <div className="text-center mb-1">
              <span className="small fw-semibold">{teamAcronym(data.home)} v {teamAcronym(data.away)}</span>
              <span className="text-muted small ms-2">{formatDate(data.matchDate)}</span>
            </div>
            <div className="text-center text-muted" style={{ fontSize: '0.6em' }}>
              Home goals (rows) v Away goals (cols) &mdash; drag to rotate
            </div>
            <Matrix3D
              matrix={matrix6x6}
              rowLabels={[0, 1, 2, 3, 4, 5]}
              colLabels={[0, 1, 2, 3, 4, 5]}
              maxHeight={5}
              colorFn={scoreColorFn}
              height={260}
            />
          </>
        )}
      </div>
    </div>
  );
}
